import { useQueryClient } from '@tanstack/react-query'
import { CurecallToggle, ECurecallNotificationAppearance, ECurecallSize } from '@honestica/curecall-ui'
import { useEffect, useState } from 'react'
import { PatientService } from 'src/services/patient.service'
import List, { ListItem } from '../list/list.component'
import { useParams } from 'react-router-dom'
import { useNotification } from 'src/providers/notification.provider'
import { useTranslation } from 'react-i18next'
import { useCurrentPatientFile } from 'src/pages/patients/patient-file/_components/patient-file.provider'
import Card from 'src/components/card/card.component'
import { getTranslatedLabel } from 'src/utils/locale.util'
import { TNavigationLocales } from 'src/types/locales'
import { TVariable } from 'src/types/variable'
import { EFormType, TPatientForm } from 'src/types/form'
import { getErrorMessage } from 'src/utils/http.util'

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

const TogglePatientVariable = ({
  defaultEnabled,
  onChange,
}: {
  defaultEnabled: boolean
  onChange: (enabled: boolean) => void
}) => {
  const [serverEnabled, setServerEnabled] = useState(defaultEnabled)
  const [enabled, setEnabled] = useState(defaultEnabled)

  const debouncedEnabled = useDebounce(enabled, 1000)

  useEffect(() => {
    if (serverEnabled !== debouncedEnabled) {
      setServerEnabled(debouncedEnabled)
      onChange(debouncedEnabled)
    }
  }, [debouncedEnabled, serverEnabled, onChange])

  return (
    <CurecallToggle
      size={ECurecallSize.LARGE}
      isEnabled={enabled}
      onChange={() => setEnabled((prev) => !prev)}
    />
  )
}

const GoodToKnowFormCard = () => {
  const queryClient = useQueryClient()
  const params = useParams()
  const projectId = Number(params.projectId)
  const patientId = Number(params.patientId)
  const { notify } = useNotification()
  const { t, i18n } = useTranslation()
  const locale = i18n.language as TNavigationLocales

  const { patientAllVariables, currentPatientFollowup } = useCurrentPatientFile()

  const correspondingPatientForm = patientAllVariables?.patientForms?.find(
    (patientForm: TPatientForm) => patientForm?.Form?.type === EFormType.GOOD_TO_KNOW,
  )

  if (!correspondingPatientForm || !correspondingPatientForm.Form?.FormVariables) {
    return null
  }

  const editPatientVariable = async (VariableId: number, patientAnswer: string) => {
    try {
      const result = await PatientService.postPatientFormsVariable(
        projectId,
        patientId,
        correspondingPatientForm?.id,
        {
          VariableId,
          patientAnswer,
        },
      )
      queryClient.setQueryData<{ patientForms: TPatientForm[] }>(
        ['patients-all-variables', projectId, patientId],
        (oldData) => {
          if (!oldData) return { patientForms: [] }

          const oldPatientForms = oldData.patientForms
          const updatedPatientForms = oldPatientForms.map((patientForm) => {
            if (patientForm.id === correspondingPatientForm.id) {
              const isUpdatedPatientVariableInList = patientForm.PatientVariables.some(
                (pv) => pv.VariableId === result.VariableId,
              )
              const PatientVariables = isUpdatedPatientVariableInList
                ? patientForm.PatientVariables.map((pv) =>
                    pv.VariableId === result.VariableId ? result : pv,
                  )
                : [...patientForm.PatientVariables, result]
              return { ...patientForm, PatientVariables }
            } else {
              return patientForm
            }
          })
          return {
            ...oldData,
            patientForms: updatedPatientForms,
          }
        },
      )
    } catch (e) {
      notify({
        message: getErrorMessage(e) || t('pages.patient-page.edit-variable-error'),
        title: t('common.actions.error'),
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    }
  }

  const list: ListItem[] = correspondingPatientForm.Form?.Variables?.map((variable: TVariable) => {
    const patientVariable = correspondingPatientForm.PatientVariables.find(
      (pvariable) => pvariable.VariableId === variable.id,
    )
    const value =
      patientVariable?.value !== null && patientVariable?.value !== undefined ? (
        <TogglePatientVariable
          defaultEnabled={patientVariable?.value === true || patientVariable?.value === '1'}
          onChange={(enabled) => {
            editPatientVariable(variable.id, enabled ? '1' : '0')
          }}
        />
      ) : (
        <button
          className="c-text-primary-600 c-text-sm c-font-bold hover:c-text-primary-300"
          onClick={() => editPatientVariable(variable.id, '1')}
        >
          {t('pages.patient-page.activate')}
        </button>
      )

    return {
      label: getTranslatedLabel(locale, variable.intlLabel) || '',
      value,
    }
  })

  const introText =
    getTranslatedLabel(
      locale,
      currentPatientFollowup?.Followup?.configUI?.followupUI?.patientDetails?.textIntro,
    ) || ''

  return (
    <Card title={getTranslatedLabel(locale, correspondingPatientForm?.Form?.intlLabel)} className="c-pb-4">
      {introText && <div className="c-text-muted c-pt-2 c-mb-6">{introText}</div>}
      <div className="c-pt-2">
        <List list={list} />
      </div>
    </Card>
  )
}

export default GoodToKnowFormCard
